import { useMemo } from "react";
import { useGetIdentity, useGetList, useGetMany } from "react-admin";
import { ResourceName, Task, StoplossClaim, Claimant, Client, TaskStatus } from "@/types";
import { useStoplossClaimStats } from "./useStoplossClaimsStats";
import { isOverdue, isToday, isThisWeek } from "@/utils/helpers/dateUtils";
import { IN_PROCESS_STATUSES } from "@/constants/stoplossClaimsGroupings";

/** Resolve a claimant display name from available name fields */
export const getClaimantDisplayName = (
  claimant?: Partial<Pick<Claimant, "claimant_name" | "claimant_first_name" | "claimant_last_name">>
): string | undefined => {
  if (!claimant) return undefined;
  if (claimant.claimant_name) return claimant.claimant_name;
  if (claimant.claimant_first_name || claimant.claimant_last_name) {
    return [claimant.claimant_first_name, claimant.claimant_last_name].filter(Boolean).join(" ");
  }
  return undefined;
};

export interface ClaimWithContext extends StoplossClaim {
  claimantName?: string;
  clientName?: string;
}

export type MonthlyStat = {
  name: string;
  claims: number;
  reimbursed: number;
  amount: number;
  /** Prior year claim count (only populated when a specific year is selected) */
  priorYearClaims?: number;
};

export type StatusStat = {
  name: string;
  value: {
    claims: number;
    amount: number;
  };
};

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const useLandingPageData = (selectedYear?: number) => {
  const { data: identity } = useGetIdentity();
  const userId = identity?.id;
  const userName = identity?.fullName?.split(" ")[0] || "there";

  const { stats, isLoading: statsLoading } = useStoplossClaimStats(selectedYear);

  const { data: allTasks = [], isLoading: tasksLoading } = useGetList<Task>(
    `users/${userId}/tasks`,
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "due_date", order: "ASC" },
    },
    { enabled: !!userId }
  );

  const tasks = useMemo(() => allTasks.filter((t) => t.status !== TaskStatus.COMPLETED), [allTasks]);

  // Urgency buckets
  const overdueTasks = useMemo(() => tasks.filter((t) => t.due_date && isOverdue(t.due_date)), [tasks]);
  const todayTasks = useMemo(() => tasks.filter((t) => t.due_date && isToday(t.due_date)), [tasks]);
  const upcomingTasks = useMemo(
    () => tasks.filter((t) => t.due_date && isThisWeek(t.due_date) && !isToday(t.due_date) && !isOverdue(t.due_date)),
    [tasks]
  );
  const laterTasks = useMemo(
    () => tasks.filter((t) => !t.due_date || (!isOverdue(t.due_date) && !isThisWeek(t.due_date))),
    [tasks]
  );

  // All claims for charts — server-side filtered by year via created_datetime range
  const claimsFilter = useMemo(() => {
    if (!selectedYear) return {};
    return {
      created_datetime_gte: new Date(selectedYear, 0, 1).toISOString(),
      created_datetime_lt: new Date(selectedYear + 1, 0, 1).toISOString(),
    };
  }, [selectedYear]);

  const { data: allClaims = [], isLoading: chartClaimsLoading } = useGetList<StoplossClaim>(
    ResourceName.STOPLOSS_CLAIMS,
    {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: "created_datetime", order: "DESC" },
      filter: claimsFilter,
    }
  );

  // Prior year claims for YoY comparison (only fetched when a specific year is selected)
  const priorYearFilter = useMemo(() => {
    if (!selectedYear) return {};
    const priorYear = selectedYear - 1;
    return {
      created_datetime_gte: new Date(priorYear, 0, 1).toISOString(),
      created_datetime_lt: new Date(priorYear + 1, 0, 1).toISOString(),
    };
  }, [selectedYear]);

  const { data: priorYearClaims = [] } = useGetList<StoplossClaim>(
    ResourceName.STOPLOSS_CLAIMS,
    {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: "created_datetime", order: "DESC" },
      filter: priorYearFilter,
    },
    { enabled: !!selectedYear }
  );

  // Monthly trend data for activity chart (with optional prior year overlay)
  const REIMBURSED_STATUSES: string[] = ["reimbursed", "partially_reimbursed"];
  const claimsByMonth: MonthlyStat[] = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const claimsInMonth = allClaims.filter((c) => {
        if (c.created_datetime) {
          return new Date(c.created_datetime).getMonth() === i;
        }
        return false;
      });

      const priorInMonth = selectedYear
        ? priorYearClaims.filter((c) => c.created_datetime && new Date(c.created_datetime).getMonth() === i)
        : [];

      return {
        name: MONTH_NAMES[i],
        claims: claimsInMonth.length,
        reimbursed: claimsInMonth.filter((c) => REIMBURSED_STATUSES.includes(c.claim_status)).length,
        amount: claimsInMonth.reduce((sum, c) => sum + (c.total_paid_amount || 0), 0),
        ...(selectedYear && { priorYearClaims: priorInMonth.length }),
      };
    });
  }, [allClaims, priorYearClaims, selectedYear]);

  // Status distribution for pipeline chart
  const claimsByStatus: StatusStat[] = useMemo(() => {
    return Object.entries(
      allClaims.reduce(
        (acc, claim) => {
          const status = claim.claim_status;
          if (!acc[status]) acc[status] = { claims: 0, amount: 0 };
          acc[status].claims += 1;
          acc[status].amount += claim.final_cumulative_total || 0;
          return acc;
        },
        {} as Record<string, { claims: number; amount: number }>
      )
    ).map(([name, value]) => ({ name, value }));
  }, [allClaims]);

  // Claims the user is working on (from tasks)
  const claimIdsFromTasks = useMemo(() => {
    return [
      ...new Set(
        allTasks
          .filter((t) => t.record?.object_type === "stoploss-claims" && t.record?.object_id)
          .map((t) => t.record!.object_id!)
      ),
    ];
  }, [allTasks]);

  const { data: workingOnClaims = [], isLoading: claimsLoading } = useGetMany<StoplossClaim>(
    ResourceName.STOPLOSS_CLAIMS,
    { ids: claimIdsFromTasks },
    { enabled: claimIdsFromTasks.length > 0 }
  );

  // Actionable claims — In Process statuses (New, In Process, Pended), most recently modified first
  const actionableClaimsRaw = useMemo(() => {
    return [...allClaims]
      .filter((c) => (IN_PROCESS_STATUSES as readonly string[]).includes(c.claim_status))
      .sort((a, b) => {
        const dateA = a.modified_datetime ? new Date(a.modified_datetime).getTime() : 0;
        const dateB = b.modified_datetime ? new Date(b.modified_datetime).getTime() : 0;
        return dateB - dateA;
      });
  }, [allClaims]);

  const claimantIds = useMemo(() => {
    return [
      ...new Set([...workingOnClaims, ...actionableClaimsRaw].map((c) => c.claimant_id_ref).filter(Boolean)),
    ] as string[];
  }, [workingOnClaims, actionableClaimsRaw]);

  const clientIds = useMemo(() => {
    return [
      ...new Set([...workingOnClaims, ...actionableClaimsRaw].map((c) => c.client_id_ref).filter(Boolean)),
    ] as string[];
  }, [workingOnClaims, actionableClaimsRaw]);

  const { data: claimants = [] } = useGetMany<Claimant>(
    ResourceName.CLAIMANTS,
    { ids: claimantIds },
    { enabled: claimantIds.length > 0 }
  );

  const { data: claimClients = [] } = useGetMany<Client>(
    ResourceName.CLIENTS,
    { ids: clientIds },
    { enabled: clientIds.length > 0 }
  );

  const claimantMap = useMemo(() => new Map(claimants.map((c) => [c.id, c])), [claimants]);
  const clientMap = useMemo(() => new Map(claimClients.map((c) => [c.id, c])), [claimClients]);

  const myClaims: ClaimWithContext[] = useMemo(() => {
    return [...workingOnClaims]
      .sort((a, b) => {
        const dateA = a.modified_datetime ? new Date(a.modified_datetime).getTime() : 0;
        const dateB = b.modified_datetime ? new Date(b.modified_datetime).getTime() : 0;
        return dateB - dateA;
      })
      .map((claim) => ({
        ...claim,
        claimantName: getClaimantDisplayName(claimantMap.get(claim.claimant_id_ref)),
        clientName: clientMap.get(claim.client_id_ref)?.client_legal_name,
      }));
  }, [workingOnClaims, claimantMap, clientMap]);

  // statAmounts removed — stats now carry their own amounts (see useStoplossClaimStats)

  const actionableClaims: ClaimWithContext[] = useMemo(() => {
    return actionableClaimsRaw.map((claim) => ({
      ...claim,
      claimantName: getClaimantDisplayName(claimantMap.get(claim.claimant_id_ref)),
      clientName: clientMap.get(claim.client_id_ref)?.client_legal_name,
    }));
  }, [actionableClaimsRaw, claimantMap, clientMap]);

  return {
    userName,
    stats,
    overdueTasks,
    todayTasks,
    upcomingTasks,
    laterTasks,
    myClaims,
    actionableClaims,
    claimsByMonth,
    claimsByStatus,
    isLoading: tasksLoading || claimsLoading || statsLoading,
    chartsLoading: chartClaimsLoading,
  };
};
